import i18next from 'i18next';

/**
 * 统一的音乐下载工具函数
 * @param {number} musicId - 音乐ID
 * @param {boolean} allowDownload - 是否允许下载（可选，如果组件已检查过可省略）
 * @param {Function} onSuccess - 下载成功回调（可选，用于更新下载计数）
 * @param {Array<string>} formats - 要下载的格式数组（可选，如 ['mp3', 'opus']，默认 ['mp3']）
 * @param {boolean} includeLyrics - 是否同时下载歌词（可选，默认 false）
 * @returns {Promise<void>}
 */
export const downloadMusic = async (musicId, allowDownload = true, onSuccess = null, formats = ['mp3'], includeLyrics = false) => {
  // 检查下载权限
  if (!allowDownload) {
    alert(i18next.t('musicList.downloadNotAllowed'));
    return;
  }

  // 辅助函数：使用 fetch 下载文件并处理错误
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      
      // 如果响应不是成功的，尝试读取错误信息
      if (!response.ok) {
        let errorMessage = i18next.t('hints.downloadFailedRetry');
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // 如果无法解析 JSON，使用默认错误信息
          if (response.status === 403) {
            errorMessage = '下载失败：权限不足';
          } else if (response.status === 404) {
            errorMessage = '下载失败：文件不存在';
          }
        }
        alert(errorMessage);
        return false;
      }
      
      // 下载文件
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // 尝试从 Content-Disposition 头获取文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          const extractedFilename = matches[1].replace(/['"]/g, '');
          link.download = decodeURIComponent(extractedFilename);
        }
      } else if (filename) {
        link.download = filename;
      }
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 清理 blob URL
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      return true;
    } catch (error) {
      console.error('Download error:', error);
      alert(i18next.t('hints.downloadFailedRetry'));
      return false;
    }
  };

  try {
    // 下载所有选中的格式
    for (let i = 0; i < formats.length; i++) {
      const format = formats[i];
      const url = `/api/music/${musicId}/download?format=${format}`;
      const success = await downloadFile(url);
      if (!success) {
        // 如果第一个文件下载失败，不再尝试后续文件
        return;
      }
      // 如果不是最后一个文件，稍微延迟
      if (i < formats.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // 如果选择了下载歌词
    if (includeLyrics) {
      await new Promise(resolve => setTimeout(resolve, 500));
      await downloadFile(`/api/music/${musicId}/download?includeLyrics=true`);
    }
    
    // 下载成功，调用回调函数（用于更新下载计数）
    if (onSuccess && typeof onSuccess === 'function') {
      onSuccess();
    }
  } catch (error) {
    console.error('Download error:', error);
    alert(i18next.t('hints.downloadFailedRetry'));
  }
};

/**
 * 批量下载音乐（使用默认格式MP3）
 * @param {Array} musicList - 音乐列表（每个音乐对象需要包含 id 和 allow_download）
 * @param {Function} onSuccess - 批量下载完成回调（可选）
 * @returns {Promise<void>}
 */
export const downloadMusicsBatch = async (musicList, onSuccess = null) => {
  if (!musicList || musicList.length === 0) {
    alert(i18next.t('hints.noDownloadableMusic'));
    return;
  }

  // 过滤出允许下载的音乐
  const allowedMusics = musicList.filter(m => m.allow_download);
  
  if (allowedMusics.length === 0) {
    alert(i18next.t('hints.noAllowedDownloadMusic'));
    return;
  }

  // 逐个下载，延迟执行避免浏览器阻止
  allowedMusics.forEach((music, index) => {
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = `/api/music/${music.id}/download?format=mp3`;
      // 不设置 download 属性，让浏览器使用服务器返回的文件名
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, index * 500); // 延迟下载，避免浏览器阻止
  });
  
  alert(i18next.t('hints.startDownloading', { count: allowedMusics.length }));
  
  // 批量下载完成回调
  if (onSuccess && typeof onSuccess === 'function') {
    onSuccess(allowedMusics.length);
  }
};

